import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { GoogleMap } from "@angular/google-maps";
import { DispatchService } from "../shared/dispatch.service";
import { ProductService } from "@app/product/shared/products.service";
import { TerminalLocationsServices } from "@app/shared/services/terminals/terminals.service";
import { ToastrService } from "ngx-toastr";
import { GetPictureService } from "@app/shared/services/files/getPictures.service";
import { forkJoin } from "rxjs";
import { AssignDriverResult } from "@app/shared/components/assign-driver-modal/assign-driver-modal.component";

export interface DispatchFilters {
  // Trip filters
  productQualification: string | null;
  location: any | null;
  milesFromLocation: number;
  date: Date | null;

  // Driver filters
  driverStatus: "assigned" | "unassigned" | "both";
  driverType: string | null;
  driverSearch: string;

  // Trailer filters
  trailerStatus: string | null;
  trailerTerminal: string | null;
  trailerProduct: string | null;
  trailerSearch: string;
  includeTrailersOutOfService: boolean;

  // Tractor filters
  tractorStatus: string | null;
  tractorTerminal: string | null;
  tractorSearch: string;
  includeTractorsOutOfService: boolean;
}

export interface DriverCard {
  type: "driver";
  id: string;
  name: string;
  driverNo: string;
  driverType: string;
  pictureUrl: string | null;
  location: string | null;
  locationAge: string | null;
  latitude: number | null;
  longitude: number | null;
  milesLast7Days: number;
  homeCity: string | null;
  homeState: string | null;
  hoursOfService: number | null;
  assigned: boolean;
  selected: boolean;
  tractorId: string | null;
  trailerId: string | null;
  tractorNumber: string | null;
  trailerNumber: string | null;
  attachedTrailerNumbers: string[];
  schedulingTeamName: string | null;
  schedulingTeamBoardName: string | null;
  productQualifications: string[];
  prequalifiedProductQualifications: string[];
}

export interface TrailerCard {
  type: "trailer";
  id: string;
  trailerNumber: string;
  equipmentCode: string | null;
  currentLocation: string | null;
  locationAge: string | null;
  latitude: number | null;
  longitude: number | null;
  terminal: string | null;
  inService: boolean;
  assigned: boolean;
  nextServiceDate: Date | null;
  daysUntilNextService: number | null;
  selected: boolean;
  attachedTractorNumber: string | null;
}

export interface TractorCard {
  type: "tractor";
  id: string;
  tractorNumber: string;
  currentLocation: string | null;
  locationAge: string | null;
  latitude: number | null;
  longitude: number | null;
  terminal: string | null;
  inService: boolean;
  assigned: boolean;
  nextServiceDate: Date | null;
  daysUntilNextService: number | null;
  selected: boolean;
  attachedTrailerNumbers: string[];
}

export type DispatchCard = DriverCard | TrailerCard | TractorCard;

export interface MarkerTooltipData {
  cardType: "driver" | "tractor" | "trailer" | "cluster";
  // driver
  name?: string;
  driverNo?: string;
  driverType?: string;
  schedulingTeamName?: string | null;
  schedulingTeamBoardName?: string | null;
  hoursOfService?: number | null;
  milesLast7Days?: number;
  // tractor / driver shared
  tractorNumber?: string | null;
  trailerNumber?: string | null;
  attachedTrailerNumbers?: string[];
  assignedDriverName?: string | null;
  // trailer
  equipmentCode?: string | null;
  attachedTractorNumber?: string | null;
  // shared
  location?: string | null;
  locationAge?: string | null;
  terminal?: string | null;
  inService?: boolean;
  productQualifications?: string[];
  // cluster
  clusterCount?: number;
  clusterDrivers?: string[];
  clusterTractors?: string[];
  clusterTrailers?: string[];
}

export interface MapMarkerData {
  options: google.maps.MarkerOptions;
  cardIds: string[];
  isCluster: boolean;
  count: number;
  tooltipData: MarkerTooltipData;
}

@Component({
  selector: "app-dispatch-finder",
  templateUrl: "./dispatch-finder.component.html",
  styleUrls: ["./dispatch-finder.component.css"],
})
export class DispatchFinderComponent implements OnInit {
  @ViewChild(GoogleMap) map!: GoogleMap;
  @ViewChild("cardsList") cardsListRef!: ElementRef;

  // Assign Schedule Modal
  showAssignScheduleModal = false;
  assignScheduleDriver: DriverCard | null = null;
  assignScheduleTractor: TractorCard | null = null;
  assignScheduleTrailer: TrailerCard | null = null;
  assignModalExistingTeamId: string | null = null;
  assignModalExistingBoardId: string | null = null;
  assignModalExistingBillToId: string | null = null;
  schedulingTeams: any[] = [];

  filtersCollapsed = false;

  // Category visibility toggles
  categoryVisibility = {
    drivers: true,
    trailers: true,
    tractors: true,
  };

  // Map type visibility (checkboxes)
  showTypes = {
    drivers: true,
    trailers: true,
    tractors: true,
  };

  filters: DispatchFilters = {
    productQualification: null,
    location: null,
    milesFromLocation: 500,
    date: null,
    driverStatus: "both",
    driverType: null,
    driverSearch: "",
    trailerStatus: "unassigned",
    trailerTerminal: null,
    trailerProduct: null,
    trailerSearch: "",
    includeTrailersOutOfService: false,
    tractorStatus: "unassigned",
    tractorTerminal: null,
    tractorSearch: "",
    includeTractorsOutOfService: false,
  };

  // Dropdown options
  productQualifications: { label: string; value: string }[] = [];
  driverTypes: { label: string; value: string }[] = [];
  driverStatuses = [
    { label: "Assigned", value: "assigned" },
    { label: "Unassigned", value: "unassigned" },
    { label: "Both", value: "both" },
  ];
  trailerStatuses = [
    { label: "All", value: null },
    { label: "Assigned", value: "assigned" },
    { label: "Unassigned", value: "unassigned" },
  ];
  terminals: { label: string; value: string }[] = [];
  tractorStatuses = [
    { label: "All", value: null },
    { label: "Assigned", value: "assigned" },
    { label: "Unassigned", value: "unassigned" },
  ];

  // Cards data
  allCards: DispatchCard[] = []; // Unfiltered original data
  cards: DispatchCard[] = []; // Filtered/displayed cards
  driversWithTimeOff: Set<string> = new Set(); // Driver IDs with time off on selected date

  // Map configuration
  mapCenter: google.maps.LatLngLiteral = { lat: 39.8283, lng: -98.5795 }; // Center of US
  mapZoom = 4;
  mapOptions: google.maps.MapOptions = {
    mapTypeId: "roadmap",
    zoomControl: true,
    scrollwheel: true,
    disableDoubleClickZoom: false,
    maxZoom: 18,
    minZoom: 3,
  };

  // Map markers
  allMapMarkers: MapMarkerData[] = [];
  locationMarker: google.maps.MarkerOptions | null = null;
  locationCircle: google.maps.CircleOptions | null = null;

  // Tooltip
  hoveredMarker: MapMarkerData | null = null;
  tooltipX = 0;
  tooltipY = 0;

  constructor(
    private dispatchService: DispatchService,
    private productService: ProductService,
    private terminalService: TerminalLocationsServices,
    private toastr: ToastrService,
    private getPictureService: GetPictureService
  ) {}

  ngOnInit(): void {
    this.loadDropdownData();
    this.loadData();
  }

  private loadDropdownData(): void {
    forkJoin({
      productQualifications: this.productService.getProductQualifications(),
    }).subscribe({
      next: (data: any) => {
        this.productQualifications = data.productQualifications.map((pq: any) => ({
          label: pq.name,
          value: pq.name,
        }));

        // Load terminals
        const terminalList = this.terminalService.getTerminalLocations();
        this.terminals = terminalList.map((t: string) => ({
          label: t,
          value: t,
        }));
      },
      error: (error) => {
        console.error("Failed to load dropdown data:", error);
        this.toastr.error("Failed to load filter options");
      },
    });
  }

  private loadData(): void {
    const filterParams = this.buildFilterParams();

    // Load all dispatch data (drivers, trailers, tractors) in a single optimized call
    forkJoin({
      dispatch: this.dispatchService.getDispatchData(filterParams),
      teams: this.dispatchService.getAllSchedulingTeams(),
    }).subscribe({
      next: ({ dispatch: data, teams }: { dispatch: any; teams: any[] }) => {
        this.schedulingTeams = teams;
        // If date filter is set, fetch time off data
        if (this.filters.date) {
          this.dispatchService.getDriversWithTimeOff(this.filters.date).subscribe({
            next: (driverIds) => {
              this.driversWithTimeOff = new Set(driverIds);
              this.processDriverData(data);
            },
            error: (error) => {
              console.error("Failed to load time off data:", error);
              this.driversWithTimeOff = new Set();
              this.processDriverData(data);
            },
          });
        } else {
          this.driversWithTimeOff = new Set();
          this.processDriverData(data);
        }
      },
      error: (error) => {
        console.error("Failed to load data:", error);
        this.toastr.error("Failed to load dispatch data");
      },
    });
  }

  private processDriverData(data: any): void {
    // Extract unique driver types from the server response
    const uniqueDriverTypes = new Set<string>();
    data.drivers.forEach((driver: any) => {
      if (driver.driverType) {
        uniqueDriverTypes.add(driver.driverType);
      }
    });

    // Populate driver types dropdown
    this.driverTypes = Array.from(uniqueDriverTypes)
      .sort()
      .map((type) => ({
        label: this.getDriverTypeLabel(type),
        value: type,
      }));

    this.allCards = [
      ...this.mapDrivers(data.drivers),
      ...this.mapTrailers(data.trailers),
      ...this.mapTractors(data.tractors),
    ];
    this.applyFiltersLocally();
    this.updateMapMarkers();
  }

  private buildFilterParams(): any {
    return {
      driverStatus: this.filters.driverStatus,
      driverTypes: this.filters.driverType ? [this.filters.driverType] : [],
    };
  }

  private buildDriverTeamLookup(): Map<string, { teamName: string; boardName: string | null }> {
    const lookup = new Map<string, { teamName: string; boardName: string | null }>();
    for (const team of this.schedulingTeams) {
      for (const driver of team.drivers ?? []) {
        lookup.set(driver.driverId, { teamName: team.name, boardName: driver.boardName || null });
      }
    }
    return lookup;
  }

  private mapDrivers(drivers: any[]): DriverCard[] {
    const teamLookup = this.buildDriverTeamLookup();
    return drivers.map((d) => ({
      type: "driver",
      id: d.id,
      name: d.name,
      driverNo: d.driverNo,
      driverType: d.driverType,
      pictureUrl: d.pictureUrl,
      location: d.location,
      locationAge: this.calculateLocationAge(d.locationDateTime),
      latitude: d.latitude,
      longitude: d.longitude,
      milesLast7Days: d.milesLast7Days || 0,
      homeCity: d.homeCity,
      homeState: d.homeState,
      hoursOfService: d.rawHoursOfService?.dsaUsa708Hours || d.hoursOfService || null,
      assigned: d.isAssigned,
      selected: false,
      tractorId: d.tractorId,
      trailerId: d.trailerId,
      tractorNumber: d.tractorNumber || null,
      trailerNumber: d.trailerNumber || null,
      attachedTrailerNumbers: Array.isArray(d.attachedTrailerNumbers)
        ? d.attachedTrailerNumbers
        : [],
      schedulingTeamName: teamLookup.get(d.id)?.teamName || null,
      schedulingTeamBoardName: teamLookup.get(d.id)?.boardName || null,
      productQualifications: Array.isArray(d.productQualifications) ? d.productQualifications : [],
      prequalifiedProductQualifications: Array.isArray(d.prequalifiedProductQualifications)
        ? d.prequalifiedProductQualifications
        : [],
    }));
  }

  private mapTrailers(trailers: any[]): TrailerCard[] {
    return trailers.map((t) => ({
      type: "trailer",
      id: t.id,
      trailerNumber: t.trailerNumber,
      equipmentCode: t.equipmentCode,
      currentLocation: t.currentLocation,
      locationAge: this.calculateLocationAge(t.locationDateTime),
      latitude: t.latitude,
      longitude: t.longitude,
      terminal: t.terminal,
      inService: t.inService,
      assigned: t.isAssigned,
      nextServiceDate: t.nextServiceDate ? new Date(t.nextServiceDate) : null,
      daysUntilNextService: this.calculateDaysUntilService(t.nextServiceDate),
      selected: false,
      attachedTractorNumber: t.attachedTractorNumber || null,
    }));
  }

  private mapTractors(tractors: any[]): TractorCard[] {
    return tractors.map((t) => ({
      type: "tractor",
      id: t.id,
      tractorNumber: t.tractorNumber,
      currentLocation: t.currentLocation || t.parsedLocation,
      locationAge: this.calculateLocationAge(t.locationDateTime),
      latitude: t.latitude,
      longitude: t.longitude,
      terminal: t.terminal,
      inService: t.inService,
      assigned: t.isAssigned,
      nextServiceDate: t.nextServiceDate ? new Date(t.nextServiceDate) : null,
      daysUntilNextService: this.calculateDaysUntilService(t.nextServiceDate),
      selected: false,
      attachedTrailerNumbers: Array.isArray(t.attachedTrailerNumbers)
        ? t.attachedTrailerNumbers
        : [],
    }));
  }

  private calculateLocationAge(dateTime: Date | null): string | null {
    if (!dateTime) return null;

    const now = new Date();
    const locationDate = new Date(dateTime);
    const diffMs = now.getTime() - locationDate.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffHours < 1) return "< 1 hour ago";
    if (diffHours === 1) return "1 hour ago";
    if (diffHours < 24) return `${diffHours} hours ago`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return "1 day ago";
    return `${diffDays} days ago`;
  }

  private calculateDaysUntilService(serviceDate: Date | null): number | null {
    if (!serviceDate) return null;

    const now = new Date();
    const service = new Date(serviceDate);
    const diffMs = service.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    return diffDays;
  }

  toggleFilters(event: Event): void {
    event.stopPropagation();
    this.filtersCollapsed = !this.filtersCollapsed;
  }

  onShowTypeChange(): void {
    this.updateMapMarkers();
  }

  toggleCategory(category: "drivers" | "trailers" | "tractors"): void {
    this.categoryVisibility[category] = !this.categoryVisibility[category];
  }

  getCategoryCards(type: "driver" | "trailer" | "tractor"): DispatchCard[] {
    return this.cards.filter((card) => card.type === type);
  }
  getSelectedInCategory(type: "driver" | "trailer" | "tractor"): DispatchCard | null {
    const selected = this.cards.find((card) => card.type === type && card.selected);
    return selected || null;
  }
  applyFilters(): void {
    this.loadData();
  }

  private applyFiltersLocally(): void {
    // Start with all cards
    const filteredDrivers = this.filterDriverCards(
      this.allCards.filter((c) => c.type === "driver") as DriverCard[]
    );
    const filteredTrailers = this.filterTrailerCards(
      this.allCards.filter((c) => c.type === "trailer") as TrailerCard[]
    );
    const filteredTractors = this.filterTractorCards(
      this.allCards.filter((c) => c.type === "tractor") as TractorCard[]
    );

    this.cards = [...filteredDrivers, ...filteredTrailers, ...filteredTractors];
    this.updateMapMarkers();
  }

  private filterDriverCards(drivers: DriverCard[]): DriverCard[] {
    let filtered = [...drivers];

    // Driver Status filter
    if (this.filters.driverStatus === "assigned") {
      filtered = filtered.filter((d) => d.assigned);
    } else if (this.filters.driverStatus === "unassigned") {
      filtered = filtered.filter((d) => !d.assigned);
    }

    // Driver Type filter
    if (this.filters.driverType) {
      filtered = filtered.filter((d) => d.driverType === this.filters.driverType);
    }

    // Text search filter
    if (this.filters.driverSearch && this.filters.driverSearch.trim()) {
      const search = this.filters.driverSearch.toLowerCase().trim();
      filtered = filtered.filter(
        (d) => d.name.toLowerCase().includes(search) || d.driverNo.toLowerCase().includes(search)
      );
    }

    // Product Qualification filter
    if (this.filters.productQualification) {
      filtered = filtered.filter(
        (d) =>
          d.productQualifications.some(
            (q) => q.toLowerCase() === this.filters.productQualification?.toLowerCase()
          ) ||
          d.prequalifiedProductQualifications.some(
            (q) => q.toLowerCase() === this.filters.productQualification?.toLowerCase()
          )
      );
    }

    // Location filter (distance-based)
    if (this.filters.location) {
      const locationLat = this.filters.location.latitude || this.filters.location.Latitude;
      const locationLng = this.filters.location.longitude || this.filters.location.Longitude;

      if (locationLat && locationLng) {
        const maxDistance = this.filters.milesFromLocation || 500;
        filtered = filtered.filter((d) => {
          if (!d.latitude || !d.longitude) return false;
          const distance = this.calculateDistance(
            locationLat,
            locationLng,
            d.latitude,
            d.longitude
          );
          return distance <= maxDistance;
        });
      }
    }

    // Date filter (filter out drivers with time off on selected date)
    if (this.filters.date) {
      filtered = filtered.filter((d) => !this.driversWithTimeOff.has(d.id));
    }

    return filtered;
  }

  private filterTrailerCards(trailers: TrailerCard[]): TrailerCard[] {
    let filtered = [...trailers];

    // Trailer Status filter
    if (this.filters.trailerStatus) {
      if (this.filters.trailerStatus === "assigned") {
        filtered = filtered.filter((t) => t.assigned);
      } else if (this.filters.trailerStatus === "unassigned") {
        filtered = filtered.filter((t) => !t.assigned);
      }
    }

    // Include out of service filter
    if (!this.filters.includeTrailersOutOfService) {
      filtered = filtered.filter((t) => t.inService);
    }

    // Trailer Terminal filter
    if (this.filters.trailerTerminal) {
      filtered = filtered.filter((t) => t.terminal === this.filters.trailerTerminal);
    }

    // Trailer Product filter
    if (this.filters.trailerProduct) {
      filtered = filtered.filter((t) => t.equipmentCode === this.filters.trailerProduct);
    }

    // Text search filter
    if (this.filters.trailerSearch && this.filters.trailerSearch.trim()) {
      const search = this.filters.trailerSearch.toLowerCase().trim();
      filtered = filtered.filter((t) => t.trailerNumber.toLowerCase().includes(search));
    }

    // Location filter (distance-based) - Note: Most trailers don't have lat/lng coordinates
    if (this.filters.location) {
      const locationLat = this.filters.location.latitude || this.filters.location.Latitude;
      const locationLng = this.filters.location.longitude || this.filters.location.Longitude;

      if (locationLat && locationLng) {
        const maxDistance = this.filters.milesFromLocation || 500;
        filtered = filtered.filter((t) => {
          if (!t.latitude || !t.longitude) return false;
          const distance = this.calculateDistance(
            locationLat,
            locationLng,
            t.latitude,
            t.longitude
          );
          return distance <= maxDistance;
        });
      }
    }

    return filtered;
  }

  private filterTractorCards(tractors: TractorCard[]): TractorCard[] {
    let filtered = [...tractors];

    // Tractor Status filter
    if (this.filters.tractorStatus) {
      if (this.filters.tractorStatus === "assigned") {
        filtered = filtered.filter((t) => t.assigned);
      } else if (this.filters.tractorStatus === "unassigned") {
        filtered = filtered.filter((t) => !t.assigned);
      }
    }

    // Include out of service filter
    if (!this.filters.includeTractorsOutOfService) {
      filtered = filtered.filter((t) => t.inService);
    }

    // Tractor Terminal filter
    if (this.filters.tractorTerminal) {
      filtered = filtered.filter((t) => t.terminal === this.filters.tractorTerminal);
    }

    // Text search filter
    if (this.filters.tractorSearch && this.filters.tractorSearch.trim()) {
      const search = this.filters.tractorSearch.toLowerCase().trim();
      filtered = filtered.filter((t) => t.tractorNumber.toLowerCase().includes(search));
    }

    // Location filter (distance-based)
    if (this.filters.location) {
      const locationLat = this.filters.location.latitude || this.filters.location.Latitude;
      const locationLng = this.filters.location.longitude || this.filters.location.Longitude;

      if (locationLat && locationLng) {
        const maxDistance = this.filters.milesFromLocation || 500;
        filtered = filtered.filter((t) => {
          if (!t.latitude || !t.longitude) return false;
          const distance = this.calculateDistance(
            locationLat,
            locationLng,
            t.latitude,
            t.longitude
          );
          return distance <= maxDistance;
        });
      }
    }

    return filtered;
  }

  onDriverFilterChange(): void {
    this.applyFiltersLocally();
  }

  onTrailerFilterChange(): void {
    this.applyFiltersLocally();
  }

  onTractorFilterChange(): void {
    this.applyFiltersLocally();
  }

  clearFilters(): void {
    this.filters = {
      productQualification: null,
      location: null,
      milesFromLocation: 500,
      date: null,
      driverStatus: "both",
      driverType: null,
      driverSearch: "",
      trailerStatus: "unassigned",
      trailerTerminal: null,
      trailerProduct: null,
      trailerSearch: "",
      includeTrailersOutOfService: false,
      tractorStatus: "unassigned",
      tractorTerminal: null,
      tractorSearch: "",
      includeTractorsOutOfService: false,
    };
    this.locationMarker = null;
    this.locationCircle = null;
    this.applyFiltersLocally();
    this.updateMapMarkers();
  }

  toggleCardSelection(card: DispatchCard): void {
    // Single selection behavior - deselect all cards of the same type
    this.cards.forEach((c) => {
      if (c.type === card.type && c.id !== card.id) {
        c.selected = false;
      }
    });
    card.selected = !card.selected;
    this.updateMapMarkers();
  }

  selectAll(): void {
    this.cards.forEach((card) => (card.selected = true));
    this.updateMapMarkers();
  }

  deselectAll(): void {
    this.cards.forEach((card) => (card.selected = false));
    this.updateMapMarkers();
  }

  getSelectedCards(): DispatchCard[] {
    return this.cards.filter((card) => card.selected);
  }

  assignSelected(): void {
    const selected = this.getSelectedCards();
    const selectedDriver = selected.find((c) => c.type === "driver") as DriverCard;

    if (!selectedDriver) {
      this.toastr.warning("Please select a driver to assign to a scheduling team.");
      return;
    }

    this.assignScheduleDriver = selectedDriver;
    this.assignScheduleTractor = (selected.find((c) => c.type === "tractor") as TractorCard) || null;
    this.assignScheduleTrailer = (selected.find((c) => c.type === "trailer") as TrailerCard) || null;

    // Resolve existing team assignment for this driver
    this.assignModalExistingTeamId = null;
    this.assignModalExistingBoardId = null;
    this.assignModalExistingBillToId = null;
    for (const team of this.schedulingTeams) {
      const driverEntry = (team.drivers ?? []).find((d: any) => d.driverId === selectedDriver.id);
      if (driverEntry) {
        this.assignModalExistingTeamId = team.id;
        this.assignModalExistingBoardId = driverEntry.boardId || null;
        this.assignModalExistingBillToId = driverEntry.billToClientId || null;
        break;
      }
    }

    this.showAssignScheduleModal = true;
  }

  onAssignOrUpdateSuccess(_result: AssignDriverResult): void {
    this.doEquipmentAssignment();
  }

  onUpdateSuccess(): void {
    this.deselectAll();
    this.loadData();
  }

  onUnassignSuccess(): void {
    this.deselectAll();
    this.loadData();
  }

  private doEquipmentAssignment(): void {
    const driverId = this.assignScheduleDriver?.id;
    if (!driverId) return;

    const tractorId = this.assignScheduleTractor?.id || null;
    const trailerId = this.assignScheduleTrailer?.id || null;

    if (!tractorId && !trailerId) {
      this.deselectAll();
      this.loadData();
      return;
    }

    const tractorIds = tractorId ? [tractorId] : [];
    const trailerIds = trailerId ? [trailerId] : [];

    const doAssign = () => {
      this.dispatchService.assignItems([driverId], tractorIds, trailerIds).subscribe({
        next: (result: any) => {
          this.deselectAll();
          if (result?.displacedDrivers?.length) {
            for (const d of result.displacedDrivers) {
              this.toastr.warning(
                `${d.name || d.Name} (${d.driverNo || d.DriverNo}) had their ${d.reason || d.Reason}`,
                "Equipment reassigned"
              );
            }
          }
          this.loadData();
        },
        error: () => {
          this.toastr.warning("Driver assigned to scheduling team, but equipment assignment failed");
          this.deselectAll();
          this.loadData();
        },
      });
    };

    this.dispatchService.assignItems([driverId], tractorIds, trailerIds, true).subscribe({
      next: (preview: any) => {
        const displaced: any[] = preview?.displacedDrivers || [];
        if (displaced.length > 0) {
          const names = displaced
            .map((d: any) => `${d.name || d.Name} (${d.driverNo || d.DriverNo})`)
            .join(", ");
          const confirmed = window.confirm(
            `This will remove the equipment assignment from: ${names}.\n\nContinue?`
          );
          if (!confirmed) return;
        }
        doAssign();
      },
      error: () => doAssign(),
    });
  }

  getDriverTypeLabel(type: string): string {
    switch (type) {
      case "COMPANY DRIVER":
        return "Company Driver";
      case "LEASE OPERATOR":
        return "Lessor";
      case "OWNER OPERATOR":
        return "Owner Operator";
      default:
        // Convert "SOME_TYPE" or "SOME TYPE" to "Some Type"
        return type
          .split(/[\s_]+/)
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(" ");
    }
  }

  getDriverPictureUrl(card: DriverCard): string {
    if (card.pictureUrl) {
      return this.getPictureService.getPicturePath(card.pictureUrl, "driver");
    }
    return "assets/img/tractordriver.jpg";
  }

  private updateMapMarkers(): void {
    this.allMapMarkers = [];

    // Update location marker if location filter is set
    if (this.filters.location) {
      const locationLat = this.filters.location.latitude || this.filters.location.Latitude;
      const locationLng = this.filters.location.longitude || this.filters.location.Longitude;
      const locationName = this.filters.location.name || this.filters.location.Name;

      if (locationLat && locationLng) {
        this.locationMarker = {
          position: {
            lat: locationLat,
            lng: locationLng,
          },
          title: locationName || "Selected Location",
          icon: {
            url: "https://maps.google.com/mapfiles/ms/icons/yellow-dot.png",
          },
        };

        // Add circle representing the radius
        const radiusInMeters = (this.filters.milesFromLocation || 500) * 1609.34; // Convert miles to meters
        this.locationCircle = {
          center: {
            lat: locationLat,
            lng: locationLng,
          },
          radius: radiusInMeters,
          strokeColor: "#FFA500",
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: "#FFA500",
          fillOpacity: 0.15,
        };

        // Zoom to location
        this.mapCenter = {
          lat: locationLat,
          lng: locationLng,
        };
        this.mapZoom = 8;
      } else {
        this.locationMarker = null;
        this.locationCircle = null;
      }
    } else {
      this.locationMarker = null;
      this.locationCircle = null;
    }

    // Collect cards with valid positions, filtering by showTypes
    const cardsWithPos: { card: DispatchCard; lat: number; lng: number }[] = [];
    this.cards.forEach((card) => {
      if (card.latitude != null && card.longitude != null) {
        if (card.type === "driver" && !this.showTypes.drivers) return;
        if (card.type === "trailer" && !this.showTypes.trailers) return;
        if (card.type === "tractor" && !this.showTypes.tractors) return;
        cardsWithPos.push({ card, lat: card.latitude, lng: card.longitude });
      }
    });

    // Build reverse lookups so tractor/trailer tooltips can show the assigned driver
    const tractorDriverMap = new Map<string, DriverCard>();
    const trailerDriverMap = new Map<string, DriverCard>();
    (this.cards.filter((c) => c.type === "driver") as DriverCard[]).forEach((d) => {
      if (d.tractorId) tractorDriverMap.set(d.tractorId, d);
      if (d.trailerId) trailerDriverMap.set(d.trailerId, d);
    });

    // Grid-based clustering: group cards within the same zoom-dependent cell
    const currentZoom = this.map?.getZoom() ?? this.mapZoom;
    const threshold = this.getClusterThreshold(currentZoom);
    const clusterMap = new Map<string, typeof cardsWithPos>();
    cardsWithPos.forEach((item) => {
      const gridKey = `${Math.floor(item.lat / threshold)},${Math.floor(item.lng / threshold)}`;
      if (!clusterMap.has(gridKey)) clusterMap.set(gridKey, []);
      clusterMap.get(gridKey)!.push(item);
    });

    clusterMap.forEach((items) => {
      const count = items.length;
      const centerLat = items.reduce((s, i) => s + i.lat, 0) / count;
      const centerLng = items.reduce((s, i) => s + i.lng, 0) / count;
      const position = { lat: centerLat, lng: centerLng };
      const cardIds = items.map((i) => i.card.id);
      const hasSelected = items.some((i) => i.card.selected);

      if (count > 1) {
        // Cluster marker: list all units in tooltip
        const color = hasSelected ? "#7B1FA2" : "#37474F";

        const driverNames = items
          .filter((i) => i.card.type === "driver")
          .map((i) => (i.card as DriverCard).name);
        const tractorNumbers = items
          .filter((i) => i.card.type === "tractor")
          .map((i) => (i.card as TractorCard).tractorNumber);
        const trailerNumbers = items
          .filter((i) => i.card.type === "trailer")
          .map((i) => (i.card as TrailerCard).trailerNumber);
        const lines: string[] = [];
        if (driverNames.length > 0) lines.push(`DRIVERS: ${driverNames.join(", ")}`);
        if (tractorNumbers.length > 0) lines.push(`TRACTORS: ${tractorNumbers.join(", ")}`);
        if (trailerNumbers.length > 0) lines.push(`TRAILERS: ${trailerNumbers.join(", ")}`);
        const title = lines.join("\n");

        const fontSize = count >= 100 ? "9" : count >= 10 ? "11" : "13";
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><circle cx="20" cy="20" r="18" fill="${color}" fill-opacity="0.9" stroke="white" stroke-width="2"/><text x="20" y="25" text-anchor="middle" fill="white" font-family="Arial,sans-serif" font-size="${fontSize}" font-weight="bold">${count}</text></svg>`;

        this.allMapMarkers.push({
          options: {
            position,
            title,
            icon: {
              url: `data:image/svg+xml,${encodeURIComponent(svg)}`,
              scaledSize: new google.maps.Size(40, 40),
              anchor: new google.maps.Point(20, 20),
            },
          },
          cardIds,
          isCluster: true,
          count,
          tooltipData: {
            cardType: "cluster",
            clusterCount: count,
            clusterDrivers: driverNames,
            clusterTractors: tractorNumbers,
            clusterTrailers: trailerNumbers,
          },
        });
      } else {
        // Individual marker
        const card = items[0].card;
        let title = "";
        let dotUrl = "";

        let tooltipData: MarkerTooltipData;
        if (card.type === "driver") {
          const d = card as DriverCard;
          title = `${d.name} (${d.driverNo})`;
          dotUrl = hasSelected
            ? "https://maps.google.com/mapfiles/ms/icons/purple-dot.png"
            : "https://maps.google.com/mapfiles/ms/icons/blue-dot.png";
          tooltipData = {
            cardType: "driver",
            name: d.name,
            driverNo: d.driverNo,
            driverType: d.driverType,
            schedulingTeamName: d.schedulingTeamName,
            schedulingTeamBoardName: d.schedulingTeamBoardName,
            location: d.location,
            locationAge: d.locationAge,
            hoursOfService: d.hoursOfService,
            milesLast7Days: d.milesLast7Days,
            tractorNumber: d.tractorNumber,
            trailerNumber: d.trailerNumber,
            productQualifications: d.productQualifications,
          };
        } else if (card.type === "tractor") {
          const t = card as TractorCard;
          title = t.tractorNumber;
          dotUrl = hasSelected
            ? "https://maps.google.com/mapfiles/ms/icons/purple-dot.png"
            : "https://maps.google.com/mapfiles/ms/icons/red-dot.png";
          const assignedDriver = tractorDriverMap.get(t.id);
          tooltipData = {
            cardType: "tractor",
            tractorNumber: t.tractorNumber,
            location: t.currentLocation,
            locationAge: t.locationAge,
            terminal: t.terminal,
            inService: t.inService,
            attachedTrailerNumbers: t.attachedTrailerNumbers,
            assignedDriverName: assignedDriver ? assignedDriver.name : null,
          };
        } else {
          const t = card as TrailerCard;
          title = t.trailerNumber;
          dotUrl = hasSelected
            ? "https://maps.google.com/mapfiles/ms/icons/purple-dot.png"
            : "https://maps.google.com/mapfiles/ms/icons/green-dot.png";
          const assignedDriver = trailerDriverMap.get(t.id);
          tooltipData = {
            cardType: "trailer",
            trailerNumber: t.trailerNumber,
            equipmentCode: t.equipmentCode,
            location: t.currentLocation,
            locationAge: t.locationAge,
            terminal: t.terminal,
            inService: t.inService,
            attachedTractorNumber: t.attachedTractorNumber,
            assignedDriverName: assignedDriver ? assignedDriver.name : null,
          };
        }

        this.allMapMarkers.push({
          options: { position, title, icon: { url: dotUrl } },
          cardIds,
          isCluster: false,
          count: 1,
          tooltipData,
        });
      }
    });
  }

  private getClusterThreshold(zoom: number): number {
    // At max zoom, use 1px so only markers at literally the same coordinates get grouped.
    // Otherwise use a ~50px grid.
    const degreesPerPixel = 360 / (256 * Math.pow(2, Math.max(0, zoom)));
    const pixelSize = zoom >= 18 ? 1 : 50;
    return degreesPerPixel * pixelSize;
  }

  onMapZoomChanged(): void {
    this.updateMapMarkers();
  }

  onMapMouseMove(event: MouseEvent): void {
    this.tooltipX = event.clientX + 16;
    this.tooltipY = event.clientY + 16;
  }

  onMarkerMouseOver(markerData: MapMarkerData, event: google.maps.MapMouseEvent): void {
    this.hoveredMarker = markerData;
    if (event.domEvent instanceof MouseEvent) {
      this.tooltipX = event.domEvent.clientX + 16;
      this.tooltipY = event.domEvent.clientY + 16;
    }
  }

  onMarkerMouseOut(): void {
    this.hoveredMarker = null;
  }

  onMarkerClick(markerData: MapMarkerData): void {
    if (markerData.cardIds.length === 0) return;

    if (markerData.isCluster) {
      const gmap = this.map?.googleMap;
      const pos = markerData.options.position as google.maps.LatLngLiteral;
      const currentZoom = gmap?.getZoom() ?? this.mapZoom;
      const newZoom = Math.min(currentZoom + 3, 18);
      setTimeout(() => {
        this.mapCenter = pos;
        this.mapZoom = newZoom;
        if (gmap) {
          gmap.setCenter(pos);
          gmap.setZoom(newZoom);
        }
      }, 0);
      return;
    }

    // Individual marker - select and scroll
    const matchingCards = this.cards.filter((c) => markerData.cardIds.includes(c.id));
    if (matchingCards.length === 0) return;

    // Select highest-priority card: driver > tractor > trailer
    const card =
      matchingCards.find((c) => c.type === "driver") ||
      matchingCards.find((c) => c.type === "tractor") ||
      matchingCards[0];

    this.cards.forEach((c) => {
      if (c.type === card.type && c.id !== card.id) c.selected = false;
    });
    card.selected = true;
    this.updateMapMarkers();
    this.scrollToCard(card);
  }

  private scrollToCard(card: DispatchCard): void {
    if (card.type === "driver") this.categoryVisibility.drivers = true;
    else if (card.type === "trailer") this.categoryVisibility.trailers = true;
    else if (card.type === "tractor") this.categoryVisibility.tractors = true;

    setTimeout(() => {
      const el = this.cardsListRef?.nativeElement?.querySelector(`[data-card-id="${card.id}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 50);
  }

  isDriverCard(card: DispatchCard): card is DriverCard {
    return card.type === "driver";
  }

  isTrailerCard(card: DispatchCard): card is TrailerCard {
    return card.type === "trailer";
  }

  isTractorCard(card: DispatchCard): card is TractorCard {
    return card.type === "tractor";
  }

  // Location picker handler
  onLocationSelected(location: any): void {
    this.filters.location = location;
    this.onDriverFilterChange();
  }

  clearLocation(): void {
    this.filters.location = null;
    this.mapCenter = { lat: 39.8283, lng: -98.5795 };
    this.mapZoom = 4;
    this.onDriverFilterChange();
  }

  // Helper method to calculate distance between two lat/lng points in miles
  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 3959; // Earth's radius in miles
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) *
        Math.cos(this.toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRad(degrees: number): number {
    return (degrees * Math.PI) / 180;
  }
}
